import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../hooks/useApi';
import Icon from './Icon';
import './ModalLierTransaction.css';

function formatDate(d) {
  if (!d) return '—';
  return d.slice(0, 10);
}
function formatMontant(m) {
  if (m == null) return '—';
  return Math.abs(Number(m)).toFixed(2) + ' $';
}

export default function ModalLierTransaction({ batchId, ingredient, liaisonIdExistant, onClose, onLiee }) {
  const [suggestions, setSuggestions]   = useState([]);
  const [recentes, setRecentes]         = useState([]);
  const [resultats, setResultats]       = useState([]);
  const [recherche, setRecherche]       = useState('');
  const [selectionne, setSelectionne]   = useState(null);
  const [poidsUtilise, setPoidsUtilise] = useState('');
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [erreur, setErreur]             = useState('');

  const matiereIdParam = ingredient.id || 'null';
  const isManuelle = !ingredient.id;

  useEffect(() => {
    api.get(`/batches/${batchId}/transactions-suggestions/${matiereIdParam}`)
      .then(data => {
        setSuggestions(data.suggestions || []);
        setRecentes(data.recentes || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [batchId, matiereIdParam]);

  const rechercherTransactions = useCallback(async (q) => {
    if (!q || q.length < 2) { setResultats([]); return; }
    try {
      const data = await api.get(`/batches/${batchId}/transactions-suggestions/${matiereIdParam}?q=${encodeURIComponent(q)}`);
      setResultats(data.suggestions || []);
    } catch { setResultats([]); }
  }, [batchId, matiereIdParam]);

  const lierTransaction = async () => {
    if (!selectionne) return;
    setSaving(true);
    setErreur('');
    try {
      if (liaisonIdExistant) {
        // Mise à jour d'une liaison existante (ajoute/remplace le journal_id)
        await api.put(`/batches/liaisons/${liaisonIdExistant}`, {
          journal_id: selectionne,
          poids_utilise_kg: poidsUtilise ? parseFloat(poidsUtilise) : null,
        });
      } else {
        // Création d'une nouvelle liaison
        await api.post(`/batches/${batchId}/transactions-liees`, {
          matiere_id: ingredient.id || null,
          journal_id: selectionne,
          poids_utilise_kg: poidsUtilise ? parseFloat(poidsUtilise) : null,
          ...(isManuelle && ingredient.section_label
            ? { section_label: ingredient.section_label, section_emoji: ingredient.section_emoji || '' }
            : {}),
        });
      }
      onLiee();
    } catch (err) {
      setErreur(err.response?.data?.error || 'Erreur lors de la liaison');
      setSaving(false);
    }
  };

  const marquerRetournee = async (id, e) => {
    e.stopPropagation();
    try {
      await api.patch(`/journal/${id}/retour`, { retournee: 1 });
      setSuggestions(prev => prev.filter(t => t.id !== id));
      setRecentes(prev => prev.filter(t => t.id !== id));
      setResultats(prev => prev.filter(t => t.id !== id));
      if (selectionne === id) setSelectionne(null);
    } catch (err) {
      setErreur(err.response?.data?.error || 'Erreur lors du marquage retour');
    }
  };

  function CardTransaction({ trans }) {
    const actif = selectionne === trans.id;
    return (
      <div
        className={`lier-trans-card ${actif ? 'lier-trans-card--actif' : ''}`}
        onClick={() => setSelectionne(actif ? null : trans.id)}
      >
        <div className="lier-trans-card__entete">
          <span className="lier-trans-card__date">{formatDate(trans.date_trans)}</span>
          <span className="lier-trans-card__montant">{formatMontant(trans.montant)}</span>
        </div>
        <div className="lier-trans-card__desc">{trans.description || '—'}</div>
        <div className="lier-trans-card__fournisseur">{trans.fournisseur || ''}</div>
        <div className="lier-trans-card__footer">
          {trans.recu_fichier ? (
            <span className="lier-trans-card__recu">
              <Icon name="facture" size={12} />
              Reçu disponible
            </span>
          ) : (
            <span />
          )}
          <button
            type="button"
            className="lier-btn-retour"
            onClick={(e) => marquerRetournee(trans.id, e)}
            title="Marquer cette facture comme retournée (note de crédit) — elle disparaît de la liste à lier"
          >
            <Icon name="retour" size={11} />
            Retournée
          </button>
        </div>
      </div>
    );
  }

  const toutesListes = recherche.length >= 2 ? resultats : [...suggestions, ...recentes];

  return (
    <div className="lier-overlay" onClick={onClose}>
      <div className="lier-modal" onClick={e => e.stopPropagation()}>

        {/* En-tête */}
        <div className="lier-modal__header">
          <h3 className="lier-modal__title">
            Lier une transaction — <span className="gold">{ingredient.nom}</span>
          </h3>
          <button className="lier-modal__close" onClick={onClose} aria-label="Fermer">
            <Icon name="fermer" size={18} />
          </button>
        </div>

        {/* Corps */}
        <div className="lier-modal__body">
          {loading ? (
            <div className="lier-skel">
              <div className="skeleton lier-skel-item" />
              <div className="skeleton lier-skel-item" />
              <div className="skeleton lier-skel-item" />
            </div>
          ) : (
            <>
              <div className="lier-recherche">
                <span className="lier-recherche__icon">
                  <Icon name="recherche" size={14} />
                </span>
                <input
                  type="text"
                  placeholder="Rechercher par description ou fournisseur..."
                  value={recherche}
                  onChange={e => {
                    setRecherche(e.target.value);
                    rechercherTransactions(e.target.value);
                  }}
                  autoFocus
                />
              </div>

              {toutesListes.length === 0 ? (
                <div className="empty-state">
                  <Icon name="document" size={32} />
                  <span className="empty-state__titre">
                    {recherche.length >= 2
                      ? 'Aucun résultat pour cette recherche.'
                      : 'Aucune transaction récente trouvée.'}
                  </span>
                </div>
              ) : (
                <div className="lier-liste">
                  {recherche.length < 2 && suggestions.length > 0 && !isManuelle && (
                    <p className="lier-section-titre">
                      Suggestions basées sur « {ingredient.nom} »
                    </p>
                  )}
                  {toutesListes.map(trans => (
                    <CardTransaction key={trans.id} trans={trans} />
                  ))}
                </div>
              )}

              {selectionne && (
                <div className="lier-poids">
                  <label>
                    Quantité utilisée dans ce batch{' '}
                    <span className="muted">(optionnel)</span>
                  </label>
                  <div className="lier-poids__input">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={poidsUtilise}
                      onChange={e => setPoidsUtilise(e.target.value)}
                      placeholder="ex : 20"
                    />
                    {ingredient.unite && (
                      <span className="lier-poids__unite">{ingredient.unite}</span>
                    )}
                  </div>
                  <small className="muted">
                    Utile si tu n'as pas tout utilisé d'une seule facture
                  </small>
                </div>
              )}

              {erreur && (
                <div className="error-inline">
                  <Icon name="alerte" size={14} />
                  {erreur}
                </div>
              )}
            </>
          )}
        </div>

        {/* Pied */}
        <div className="lier-modal__footer">
          <button className="btn btn-ghost" onClick={onClose}>Annuler</button>
          <button
            className="btn btn-primary"
            onClick={lierTransaction}
            disabled={!selectionne || saving}
          >
            {saving ? 'Liaison...' : 'Lier cette transaction'}
          </button>
        </div>
      </div>
    </div>
  );
}
